import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useSnackbar } from 'notistack';
import { getDisciplineOffers } from '../../../store/actions/disciplineOfferActions';
import { fetchChamadaPdfData } from '../../../store/actions/frequenciaActions';
import { FunctionalChamadaBatchToPrint, FunctionalChamadaComponentToPrint } from './ChamadaImpressao';
import { isProfessorRole } from '../../../utils/permissions';

const ChamadasImpressao = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const printRef = useRef(null);
  const batchPrintRef = useRef(null);

  const roleName = useSelector((state) => state.auth.roleName);
  const teacherId = useSelector((state) => state.auth.teacherId);
  const disciplineOffers = useSelector((state) => state.disciplineOffers.disciplineOffers);

  const isProfessor = isProfessorRole(roleName);

  const [tabMode, setTabMode] = useState(0);
  const [filterPeriodo, setFilterPeriodo] = useState('');
  const [filterProfessor, setFilterProfessor] = useState(isProfessor && teacherId ? String(teacherId) : '');
  const [selectedDofId, setSelectedDofId] = useState('');
  const [selectedBatchItems, setSelectedBatchItems] = useState(new Set());
  const [data, setData] = useState(null);
  const [batchData, setBatchData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [error, setError] = useState('');
  const [readyToPrint, setReadyToPrint] = useState(false);
  const [readyToPrintBatch, setReadyToPrintBatch] = useState(false);

  useEffect(() => {
    dispatch(getDisciplineOffers(isProfessor ? teacherId : null));
  }, [dispatch, isProfessor, teacherId]);

  useEffect(() => {
    if (isProfessor && teacherId) {
      setFilterProfessor(String(teacherId));
    }
  }, [isProfessor, teacherId]);

  const periodos = useMemo(() => {
    const map = new Map();
    (disciplineOffers || []).forEach((dof) => {
      const desc = dof?.period?.Description;
      if (desc) map.set(desc, desc);
    });
    return Array.from(map.values()).sort();
  }, [disciplineOffers]);

  const professores = useMemo(() => {
    const map = new Map();
    (disciplineOffers || []).forEach((dof) => {
      const teacher = dof?.teacher;
      if (teacher?.id) {
        const name = `${teacher.FirstName || ''} ${teacher.LastName || ''}`.trim();
        if (!map.has(teacher.id)) map.set(teacher.id, { id: teacher.id, name });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [disciplineOffers]);

  const filteredDofs = useMemo(() => {
    let list = disciplineOffers || [];
    if (filterPeriodo) {
      list = list.filter((dof) => dof?.period?.Description === filterPeriodo);
    }
    if (filterProfessor) {
      list = list.filter((dof) => String(dof?.teacher?.id) === filterProfessor);
    }
    return list;
  }, [disciplineOffers, filterPeriodo, filterProfessor]);

  const totalBatchItems = filteredDofs.length;

  useEffect(() => {
    setSelectedDofId('');
    setSelectedBatchItems(new Set());
    setData(null);
    setBatchData([]);
    setError('');
  }, [filterPeriodo, filterProfessor]);

  const getDofLabel = (dof) => {
    const id = dof?.id || '';
    const discipline =
      dof?.disciplineModule?.discipline?.Description ||
      dof?.discipline?.Description ||
      '';
    const module = dof?.disciplineModule?.Description || '';
    const classOffer = dof?.classOffer?.Description || '';
    const dtIni = formatDate(dof?.InitialDate);
    const dtFim = formatDate(dof?.EndDate);
    const periodo = dtIni || dtFim ? ` | ${dtIni} - ${dtFim}` : '';
    return `[${id}] ${discipline} - ${module} (${classOffer})${periodo}`;
  };

  const reactToPrintContent = useCallback(() => printRef.current, [printRef]);
  const reactToPrintBatchContent = useCallback(() => batchPrintRef.current, [batchPrintRef]);

  const handlePrint = useReactToPrint({
    content: reactToPrintContent,
    documentTitle: selectedDofId ? `chamada${selectedDofId}` : 'chamada',
    removeAfterPrint: true,
    onAfterPrint: () => {
      setReadyToPrint(false);
      enqueueSnackbar('Chamada gerada com sucesso!', { variant: 'success' });
    },
  });

  const handlePrintBatch = useReactToPrint({
    content: reactToPrintBatchContent,
    documentTitle: 'Chamadas PDF - Lote',
    removeAfterPrint: true,
    onAfterPrint: () => {
      setReadyToPrintBatch(false);
      enqueueSnackbar(`${batchData.length} chamada(s) gerada(s) com sucesso!`, { variant: 'success' });
    },
  });

  useEffect(() => {
    if (readyToPrint && data && !loading) {
      const timeoutId = setTimeout(() => handlePrint(), 300);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [readyToPrint, data, loading, handlePrint]);

  useEffect(() => {
    if (readyToPrintBatch && batchData.length > 0 && !batchLoading) {
      const timeoutId = setTimeout(() => handlePrintBatch(), 300);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [readyToPrintBatch, batchData, batchLoading, handlePrintBatch]);

  const handleGerarPdf = async () => {
    const dofId = Number(selectedDofId);
    if (!dofId) {
      setError('Selecione uma Disciplina Ofertada.');
      return;
    }

    setLoading(true);
    setError('');
    setReadyToPrint(false);

    try {
      const result = await dispatch(fetchChamadaPdfData(dofId));
      setData(result);
      setReadyToPrint(true);
    } catch (e) {
      console.error('ChamadasImpressao gerar PDF ERROR', e);
      setData(null);
      setError(`Erro ao gerar o PDF da chamada: ${e?.message || 'verifique a Disciplina Ofertada e tente novamente.'}`);
    } finally {
      setLoading(false);
    }
  };

  const makeBatchKey = (dofId) => String(dofId);

  const toggleBatchItem = (dofId) => {
    const key = makeBatchKey(dofId);
    setSelectedBatchItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedBatchItems.size === totalBatchItems) {
      setSelectedBatchItems(new Set());
      return;
    }

    const all = new Set();
    filteredDofs.forEach((dof) => all.add(makeBatchKey(dof.id)));
    setSelectedBatchItems(all);
  };

  const handleGerarChamadasLote = async () => {
    if (selectedBatchItems.size === 0) return;

    setBatchLoading(true);
    setError('');
    setReadyToPrintBatch(false);

    try {
      const dofIds = Array.from(selectedBatchItems).map(Number);
      const chamadas = [];

      for (const dofId of dofIds) {
        const result = await dispatch(fetchChamadaPdfData(dofId));
        chamadas.push(result);
      }

      setBatchData(chamadas);
      setReadyToPrintBatch(true);
    } catch (e) {
      console.error('ChamadasImpressao gerar lote ERROR', e);
      setBatchData([]);
      setError(`Erro ao gerar o lote de chamadas: ${e?.message || 'tente novamente.'}`);
    } finally {
      setBatchLoading(false);
    }
  };

  const hasData = !!data;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Chamadas PDF
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Filtros
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Periodo"
              select
              value={filterPeriodo}
              onChange={(event) => setFilterPeriodo(event.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              {periodos.map((periodo) => (
                <MenuItem key={periodo} value={periodo}>
                  {periodo}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Professor"
              select
              value={filterProfessor}
              onChange={(event) => setFilterProfessor(event.target.value)}
              fullWidth
              size="small"
              disabled={isProfessor}
              helperText={isProfessor ? 'Filtrado pelo seu usuario' : ''}
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              {professores.map((professor) => (
                <MenuItem key={professor.id} value={String(professor.id)}>
                  {professor.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabMode} onChange={(_, value) => setTabMode(value)}>
          <Tab label="Individual" />
          <Tab label="Impressao em Lote" />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {tabMode === 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField
            label="Disciplina Ofertada"
            select
            value={selectedDofId}
            onChange={(event) => {
              setSelectedDofId(event.target.value);
              setData(null);
              setError('');
            }}
            fullWidth
            sx={{ mb: 2 }}
            helperText={selectedDofId ? `DOF ID: ${selectedDofId}` : `${filteredDofs.length} oferta(s) disponivel(is)`}
          >
            <MenuItem value="">
              <em>Selecione uma oferta...</em>
            </MenuItem>
            {filteredDofs.map((dof) => (
              <MenuItem key={dof.id} value={String(dof.id)}>
                {getDofLabel(dof)}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
              onClick={handleGerarPdf}
              disabled={!selectedDofId || loading}
              type="button"
            >
              {loading ? 'Gerando...' : 'Gerar pdf'}
            </Button>
            {hasData && !loading && (
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                type="button"
              >
                Imprimir novamente
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {tabMode === 1 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          {filteredDofs.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              Nenhuma disciplina ofertada encontrada para os filtros selecionados.
            </Typography>
          )}

          {filteredDofs.length > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedBatchItems.size === totalBatchItems && totalBatchItems > 0}
                      indeterminate={selectedBatchItems.size > 0 && selectedBatchItems.size < totalBatchItems}
                      onChange={toggleSelectAll}
                    />
                  }
                  label={<Typography variant="subtitle2">Selecionar Todos ({totalBatchItems} disciplinas)</Typography>}
                />
                <Typography variant="body2" color="text.secondary">
                  {selectedBatchItems.size} selecionado(s)
                </Typography>
              </Box>

              <Divider sx={{ mb: 1 }} />

              <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
                {filteredDofs.map((dof) => (
                  <FormControlLabel
                    key={dof.id}
                    sx={{ display: 'flex', alignItems: 'flex-start', py: 0.25, mr: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedBatchItems.has(makeBatchKey(dof.id))}
                        onChange={() => toggleBatchItem(dof.id)}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ pt: 0.5 }}>
                        {getDofLabel(dof)}
                      </Typography>
                    }
                  />
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Button
                variant="contained"
                startIcon={batchLoading ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
                onClick={handleGerarChamadasLote}
                disabled={selectedBatchItems.size === 0 || batchLoading}
              >
                {batchLoading
                  ? `Gerando ${selectedBatchItems.size} chamada(s)...`
                  : `Gerar ${selectedBatchItems.size} Chamada(s) em Lote`}
              </Button>
            </>
          )}
        </Paper>
      )}

      <div style={{ display: 'none' }}>
        <FunctionalChamadaComponentToPrint ref={printRef} data={data} />
        <FunctionalChamadaBatchToPrint ref={batchPrintRef} chamadas={batchData} />
      </div>
    </Box>
  );
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export default ChamadasImpressao;
